import { defineComponent as _defineComponent } from 'vue'
import {
  onMounted,
  ref,
  markRaw,
  provide,
  watch,
  useListeners,
  onBeforeUnmount,
} from 'vue'

import { apiSymbol, mapSymbol } from '@/components/core/map/utils'

type MapViewEvents = {
  (e: 'bounds_changed'): void
  (e: 'center_changed'): void
  (
    e: 'click',
    event: google.maps.MapMouseEvent | google.maps.IconMouseEvent,
  ): void
  (e: 'contextmenu', event: google.maps.MapMouseEvent): void
  (e: 'dblclick', event: google.maps.MapMouseEvent): void
  (e: 'drag'): void
  (e: 'dragend'): void
  (e: 'dragstart'): void
  (e: 'heading_changed'): void
  (e: 'idle'): void
  (e: 'isfractionalzoomenabled_changed'): void
  (e: 'mapcapabilities_changed'): void
  (e: 'maptypeid_changed'): void
  (e: 'mousemove', event: google.maps.MapMouseEvent): void
  (e: 'mouseout', event: google.maps.MapMouseEvent): void
  (e: 'mouseover', event: google.maps.MapMouseEvent): void
  (e: 'projection_changed'): void
  (e: 'renderingtype_changed'): void
  (e: 'tilesloaded'): void
  (e: 'tilt_changed'): void
  (e: 'zoom_changed'): void
}

export interface MapViewProps {
  options?: google.maps.MapOptions
  center?: google.maps.LatLng | google.maps.LatLngLiteral
  viewport?: google.maps.LatLngBounds
  zoom?: number
}

export default /*#__PURE__*/_defineComponent({
  __name: 'MapView',
  props: {
    options: null,
    center: null,
    viewport: null,
    zoom: null
  },
  emits: ["bounds_changed", "center_changed", "click", "contextmenu", "dblclick", "drag", "dragend", "dragstart", "heading_changed", "idle", "isfractionalzoomenabled_changed", "mapcapabilities_changed", "maptypeid_changed", "mousemove", "mouseout", "mouseover", "projection_changed", "renderingtype_changed", "tilesloaded", "tilt_changed", "zoom_changed"],
  setup(__props: any, { expose }: { emit: ({
  (e: 'bounds_changed'): void
  (e: 'center_changed'): void
  (
    e: 'click',
    event: google.maps.MapMouseEvent | google.maps.IconMouseEvent,
  ): void
  (e: 'contextmenu', event: google.maps.MapMouseEvent): void
  (e: 'dblclick', event: google.maps.MapMouseEvent): void
  (e: 'drag'): void
  (e: 'dragend'): void
  (e: 'dragstart'): void
  (e: 'heading_changed'): void
  (e: 'idle'): void
  (e: 'isfractionalzoomenabled_changed'): void
  (e: 'mapcapabilities_changed'): void
  (e: 'maptypeid_changed'): void
  (e: 'mousemove', event: google.maps.MapMouseEvent): void
  (e: 'mouseout', event: google.maps.MapMouseEvent): void
  (e: 'mouseover', event: google.maps.MapMouseEvent): void
  (e: 'projection_changed'): void
  (e: 'renderingtype_changed'): void
  (e: 'tilesloaded'): void
  (e: 'tilt_changed'): void
  (e: 'zoom_changed'): void
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
  options?: google.maps.MapOptions
  center?: google.maps.LatLng | google.maps.LatLngLiteral
  viewport?: google.maps.LatLngBounds
  zoom?: number
};

const defaultOptions: google.maps.MapOptions = {
  mapId: '32c2dbdab91a49eb', // Vector Based Map
  // mapId: 'b9e8fef90e5270c5', // Raster Based Map
  zoom: 4,
  center: { lat: 52.520008, lng: 13.404954 },
  fullscreenControl: false,
  mapTypeControl: false,
  streetViewControl: false,
  zoomControl: false,
}



expose({
  updateViewport,
})

const listeners = useListeners()

const mapRef = ref<HTMLElement>()
const ready = ref(false)
const map = ref<google.maps.Map>()
const api = ref<typeof google.maps>()

provide(mapSymbol, map)
provide(apiSymbol, api)

onMounted(async () => {
  if (!mapRef.value) {
    return
  }

  const mapOptions: google.maps.MapOptions = {
    ...defaultOptions,
    ...props.options,
  }

  if (props.center) {
    mapOptions.center = props.center
  }
  if (props.zoom !== undefined && props.zoom !== null) {
    mapOptions.zoom = props.zoom
  }

  const { Map } = (await google.maps.importLibrary(
    'maps',
  )) as google.maps.MapsLibrary
  api.value = markRaw(google.maps)
  map.value = markRaw(new Map(mapRef.value, mapOptions))

  if (props.viewport) {
    map.value.fitBounds(props.viewport)
  }

  Object.entries(listeners).forEach(([eventName, handler]) => {
    const handlers = Array.isArray(handler) ? handler : [handler]
    for (const handler of handlers) {
      map.value?.addListener(eventName, handler)
    }
  })
  ready.value = true
})

onBeforeUnmount(() => {
  if (map.value) {
    api.value?.event.clearInstanceListeners(map.value)
  }
})

function hasCenterChanged(
  newCenter?: google.maps.LatLng | google.maps.LatLngLiteral | null,
  oldCenter?: google.maps.LatLng | google.maps.LatLngLiteral | null,
) {
  if (!oldCenter && !newCenter) {
    return false
  }

  return (
    !oldCenter ||
    !newCenter ||
    newCenter.lng !== oldCenter.lng ||
    newCenter.lat !== oldCenter.lat
  )
}

function hasViewportChanged(
  newViewport?: google.maps.LatLngBounds | null,
  oldViewport?: google.maps.LatLngBounds | null,
) {
  if (!oldViewport && !newViewport) {
    return false
  }

  return !oldViewport || !newViewport || !newViewport.equals(oldViewport)
}

watch(
  [() => props.center, () => props.viewport, () => props.zoom] as const,
  ([newCenter, newViewport, newZoom], [oldCenter, oldViewport, oldZoom]) => {
    const centerHasChanged = hasCenterChanged(newCenter, oldCenter)
    const viewportHasChanged = hasViewportChanged(newViewport, oldViewport)

    if (newZoom !== undefined && newZoom !== oldZoom) {
      map.value?.setZoom(newZoom)
    }

    if (viewportHasChanged && newViewport) {
      map.value?.fitBounds(newViewport, 48)
    } else if (centerHasChanged && newCenter) {
      map.value?.panTo(newCenter)
    }
  },
)

function updateViewport() {
  if (props.zoom !== undefined && props.zoom !== map.value?.getZoom()) {
    map.value?.setZoom(props.zoom)
  }

  if (props.viewport) {
    map.value?.fitBounds(props.viewport, 48)
  } else if (props.center) {
    map.value?.panTo(props.center)
  }
}

return { __sfc: true,defaultOptions, props, listeners, mapRef, ready, map, api, hasCenterChanged, hasViewportChanged, updateViewport }
}

})